import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"
import { useAuthStore } from "@/features/auth/useAuthStore"
import { LoginPage } from "@/features/auth/LoginPage"
import { Layout } from "@/shared/Layout"
import type { ReactElement } from "react"
import { lazy, Suspense } from "react"
import { moduleRoutes } from "@/modules/index.generated"

interface ModuleRoute { path: string; element: ReactElement }
const appModuleRoutes = moduleRoutes as ModuleRoute[]
import { DashboardPage } from "@/features/dashboard/DashboardPage"
import { SessionDetailPage } from "@/features/analytics/SessionDetailPage"
import { ChatPage } from "@/features/chat/ChatPage"

import { LlmPage } from "@/features/llm/LlmPage"
import { CatalogPage } from "@/features/llm/CatalogPage"
import { McpPage } from "@/features/mcp/McpPage"
import { SkillsPage } from "@/features/skills/SkillsPage"
import { CredentialsPage } from "@/features/credentials/CredentialsPage"
import { SystemPage } from "@/features/system/SystemPage"
import { SettingsPage } from "@/features/system/SettingsPage"
import { SettingsHubPage } from "@/features/settings/SettingsHubPage"
import { UsersPage } from "@/features/users/UsersPage"
import { ProfilePage } from "@/features/profile/ProfilePage"
import { UiKitPage } from "@/features/uikit/UiKitPage"
import { PluginsPage } from "@/features/plugins/PluginsPage"
import { ExtensionsPage } from "@/features/extensions/ExtensionsPage"
import { ModulesPage } from "@/features/modules/ModulesPage"
import { ThemesPage } from "@/features/themes/ThemesPage"
// GrapesJS ist groß (~1 MB) → nur laden wenn der Editor wirklich geöffnet wird.
const ThemeEditorPage = lazy(() =>
  import("@/features/themeeditor/ThemeEditorPage").then((m) => ({ default: m.ThemeEditorPage })),
)
import { TemplateProofPage } from "@/features/themetemplates/TemplateProofPage"
import { ThemedPage } from "@/features/themetemplates/ThemedPage"
import { CommunicationPage } from "@/features/communication/CommunicationPage"
import { TeamchatPage } from "@/features/teamchat/TeamchatPage"
import { VMsPage } from "@/features/vms/VMsPage"
import { ContainersPage } from "@/features/containers/ContainersPage"
import { ContainerDetailPage } from "@/features/containers/ContainerDetailPage"
import { ButlerPage } from "@/features/butler/ButlerPage"
import { BuddyPage } from "@/features/buddy/BuddyPage"
import { BuddySettingsPage } from "@/features/buddy/BuddySettingsPage"
import { DataminingPage } from "@/features/datamining/DataminingPage"
import { MemoryPage } from "@/features/memory/MemoryPage"
import { JarvisMemoryPage } from "@/features/jarvismemory/JarvisMemoryPage"
import { HelpPage } from "@/features/help/HelpPage"
import { ZahnfeePage } from "@/features/zahnfee/ZahnfeePage"
import { FederationPage } from "@/features/federation/FederationPage"
import { StreamingPage } from "@/features/streaming/StreamingPage"
import { ProjectCockpitPage } from "@/features/cockpit/ProjectCockpitPage"
import { MediaCockpitPage } from "@/features/cockpit/MediaCockpitPage"
import { VaultCockpitPage } from "@/features/cockpit/VaultCockpitPage"
import { AdminCockpitPage } from "@/features/cockpit/AdminCockpitPage"
import { WerkstattCockpitPage } from "@/features/cockpit/WerkstattCockpitPage"
import { NotFoundPage } from "@/shared/NotFoundPage"
import { getLanding } from "@/features/profile/LandingSwitcher"

function Guard({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token)
  if (!token) return <Navigate to="/login" replace />
  return <>{children}</>
}

function AdminGuard({ children }: { children: React.ReactNode }) {
  const role = useAuthStore((s) => s.role)
  if (role !== "admin") return <Navigate to="/" replace />
  return <>{children}</>
}

// jarvis-r-a
import { ApprovalModal } from "@/shared/ApprovalModal"
import { ActivityReporter } from "@/shared/ActivityReporter"

export default function App() {
  return (
    <BrowserRouter>
      <ApprovalModal />
      <ActivityReporter />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <Guard>
              <Layout />
            </Guard>
          }
        >
          <Route index element={getLanding() === "dashboard" ? <DashboardPage /> : <Navigate to="/buddy" replace />} />
          <Route path="buddy" element={<ThemedPage route="buddy" fallback={<BuddyPage />} />} />
          <Route path="buddy/settings" element={<BuddySettingsPage />} />
          <Route path="dashboard" element={<ThemedPage route="dashboard" fallback={<DashboardPage />} />} />
          <Route path="analytics/session/:sid" element={<SessionDetailPage />} />
          <Route path="werkstatt" element={<WerkstattCockpitPage />} />
          <Route path="werkstatt/:sid" element={<ChatPage />} />
          <Route path="devchat" element={<Navigate to="/werkstatt" replace />} />
          {/* Agenten bleiben vorerst im Settings-Hub; Projekte bekommen ein neues Cockpit. */}
          <Route path="agents" element={<Navigate to="/settings/agents" replace />} />
          <Route path="projects" element={<ProjectCockpitPage />} />
          <Route path="media" element={<MediaCockpitPage />} />
          <Route path="vault" element={<VaultCockpitPage />} />
          <Route path="admin" element={<AdminGuard><AdminCockpitPage /></AdminGuard>} />
          <Route path="communication" element={<ThemedPage route="communication" fallback={<CommunicationPage />} />} />
          <Route path="teamchat" element={<ThemedPage route="teamchat" fallback={<TeamchatPage />} />} />
          <Route path="vms" element={<ThemedPage route="vms" fallback={<VMsPage />} />} />
          <Route path="containers" element={<ThemedPage route="containers" fallback={<ContainersPage />} />} />
          <Route path="containers/:id" element={<ContainerDetailPage />} />
          <Route path="butler" element={<ThemedPage route="butler" fallback={<ButlerPage />} />} />
          <Route path="federation" element={<ThemedPage route="federation" fallback={<FederationPage />} />} />
          <Route path="streaming" element={<ThemedPage route="streaming" fallback={<StreamingPage />} />} />
          <Route path="datamining" element={<ThemedPage route="datamining" fallback={<DataminingPage />} />} />
          <Route path="memory" element={<ThemedPage route="memory" fallback={<MemoryPage />} />} />
          <Route path="jarvis-memory" element={<JarvisMemoryPage />} />
          <Route path="llm" element={<LlmPage />} />
          <Route path="llm/catalog" element={<CatalogPage />} />
          <Route path="mcp" element={<ThemedPage route="mcp" fallback={<McpPage />} />} />
          <Route path="skills" element={<ThemedPage route="skills" fallback={<SkillsPage />} />} />
          <Route path="ui-kit" element={<UiKitPage />} />
          <Route path="credentials" element={<CredentialsPage />} />
          <Route path="settings" element={<SettingsHubPage />} />
          <Route path="settings/:groupId" element={<SettingsHubPage />} />
          <Route path="system" element={<SystemPage />} />
          <Route path="system/settings" element={<AdminGuard><SettingsPage /></AdminGuard>} />
          <Route path="users" element={<AdminGuard><UsersPage /></AdminGuard>} />
          <Route path="plugins" element={<AdminGuard><PluginsPage /></AdminGuard>} />
          <Route path="extensions" element={<AdminGuard><ExtensionsPage /></AdminGuard>} />
          <Route path="modules" element={<AdminGuard><ModulesPage /></AdminGuard>} />
          <Route path="themes" element={<AdminGuard><ThemesPage /></AdminGuard>} />
          <Route path="theme-editor" element={<AdminGuard><Suspense fallback={<div className="p-8 text-sm text-zinc-500">Editor lädt …</div>}><ThemeEditorPage /></Suspense></AdminGuard>} />
          <Route path="template-proof" element={<AdminGuard><TemplateProofPage /></AdminGuard>} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="help" element={<HelpPage />} />
          <Route path="zahnfee" element={<AdminGuard><ThemedPage route="zahnfee" fallback={<ZahnfeePage />} /></AdminGuard>} />
          {appModuleRoutes.map((r) => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
